export type MessageRole = 'USER' | 'ASSISTANT' | 'SYSTEM' | 'TOOL'

export type PermissionLevel = 'LOW' | 'MEDIUM' | 'HIGH'

export type TaskStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'WAITING_APPROVAL'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'

export type ToolExecutionStatus =
  | 'PENDING'
  | 'WAITING_APPROVAL'
  | 'RUNNING'
  | 'SUCCESS'
  | 'FAILED'
  | 'DENIED'
  | 'CANCELLED'

export interface User {
  id: string
  email: string
  name: string
  /**
   * What this account may do. Optional because a server built before roles
   * existed sends none, and the safe reading of an absent role is the ordinary
   * one -- an admin link that 403s is worse than no link.
   */
  role?: 'USER' | 'ADMIN'
}

/** One person, as the admin panel lists them. */
export interface AdminUser {
  id: string
  email: string
  name: string
  role: 'USER' | 'ADMIN'
  /** Named in the server's admin email list, so the role cannot change here. */
  lockedByConfig: boolean
  createdAt: string
}

export interface AdminUserPage {
  users: AdminUser[]
  total: number
  page: number
  pages: number
}

/** A catalogue entry as the panel sees it: identity plus the decisions made. */
export interface AdminCatalogEntry {
  id: string
  name: string
  vendor: string
  hidden: boolean
  /** Null means "wherever the catalogue puts it". */
  position: number | null
}

export interface AdminClientLimit {
  client: 'WEB' | 'MACOS' | 'WINDOWS' | 'LINUX' | 'VSCODE'
  /** Null means every visible model. */
  maxModels: number | null
  /** How many are visible in total, so a limit can be read against something. */
  available: number
}

export interface AuthResponse {
  accessToken: string
  expiresInSeconds: number
  user: User
}

/** What the client needs to build the hosted-login URL and talk to the SSO directly. */
export interface SsoConfig {
  enabled: boolean
  issuer: string | null
  clientId: string | null
  redirectUri: string | null
}

/**
 * Fixed when a conversation is created and never changed afterwards: a chat
 * switched into coding would inherit a transcript written under the general
 * prompt, and the workspace boundary would start applying halfway through work
 * it never covered.
 */
export type ConversationMode = 'GENERAL' | 'CODING'

export interface Conversation {
  id: string
  title: string
  model: string | null
  projectId: string | null
  mode: ConversationMode
  /** Absolute folders a coding conversation may touch; empty for a general one. */
  workspaceRoots: string[]
  createdAt: string
  updatedAt: string
}

export interface Message {
  id: string
  role: MessageRole
  content: string
  toolName: string | null
  toolCallId: string | null
  createdAt: string
}

export interface TurnAccepted {
  messageId: string
  taskId: string
}

export interface AgentDevice {
  id: string
  deviceName: string
  platform: string
  architecture: string
  agentVersion: string
  status: 'ONLINE' | 'OFFLINE' | 'REVOKED'
  connected: boolean
  lastSeen: string | null
}

export interface AgentRegistration {
  agentId: string
  refreshToken: string
  agentToken: string
  expiresInSeconds: number
  websocketPath: string
}

/** One row in the model dropdown: hosted or on-device, ready or downloadable. */
export interface ModelEntry {
  /** Weights filename for an on-device model; null for hosted ones. */
  file?: string | null
  id: string
  name: string
  provider: string
  kind: 'CLOUD' | 'LOCAL'
  /** Usable right now — a hosted model with a key, or a downloaded local one. */
  ready: boolean
  isDefault: boolean
  sizeBytes: number | null
  description: string | null
  /** False for models that cannot call tools reliably; the composer says so. */
  supportsTools: boolean
}

export interface ToolInfo {
  name: string
  description: string
  permission: PermissionLevel
  localAgent: boolean
}

export interface CatalogApplication {
  id: string
  name: string
  vendor: string
  category: string
  verifyCommand: string | null
  platforms: Record<string, { method: string; pkg: string | null }>
}

/** A key for the OpenAI-compatible /v1 API. The secret itself is never listed. */
export interface ApiKey {
  id: string
  name: string
  /** The first characters of the key, so keys can be told apart. */
  prefix: string
  createdAt: string
  lastUsedAt: string | null
  revokedAt: string | null
}

/** A key as created: the only time its secret is ever returned. */
export interface CreatedApiKey {
  key: ApiKey
  secret: string
}

export interface ApiUsageModel {
  model: string
  requests: number
  promptTokens: number
  completionTokens: number
}

export interface ApiUsage {
  since: string
  requests: number
  promptTokens: number
  completionTokens: number
  models: ApiUsageModel[]
}

/** Where editors should point, and which models the gateway offers. */
export interface ApiConnection {
  baseUrl: string
  models: string[]
}

export interface Memory {
  id: string
  key: string
  value: string
  updatedAt: string
}

/** A catalog model joined with what the connected device has installed. */
/** How well a model suits this machine. Ordered best to worst. */
export type FitTier =
  | 'RECOMMENDED'
  | 'WORKS'
  | 'TIGHT'
  | 'TOO_LARGE'
  /** The machine is the wrong shape for it, whatever its size. */
  | 'UNSUPPORTED'
  | 'UNKNOWN'

/**
 * What to make.
 *
 * <p>Sizes are sent rather than chosen by the server so the page can offer
 * shapes people recognise. The server clamps them: these arrive from a form,
 * and an enormous canvas is an out-of-memory crash rather than a big picture.
 */
/**
 * Whether a model can change a picture it is given.
 *
 * An ability rather than a category: an editing model is still an image model,
 * and the difference is which endpoint will answer for it.
 */
export const IMAGE_EDITING = 'image-editing'

/**
 * Whether a model can start a clip from a picture.
 *
 * The video counterpart of editing: a still goes in as the first frame and a
 * clip comes out. Different from changing a picture, and the engine takes the
 * two under different flags.
 */
export const IMAGE_TO_VIDEO = 'image-to-video'

export interface ImageRequest {
  prompt: string
  width: number
  height: number
  n: number
  /** Frames in a clip. Ignored by a model that makes stills. */
  frames?: number
}

/** One image the engine made, base64 because it came back in a JSON body. */
export interface GeneratedImage {
  data: string
  format: string
}

export interface GeneratedImages {
  modelId: string
  images: GeneratedImage[]
}

export interface LocalModel {
  id: string
  name: string
  vendor: string
  family: string
  parameters: string
  sizeBytes: number
  contextTokens: number
  capabilities: string[]
  description: string
  installed: boolean
  installedSize: number | null
  fitTier: FitTier
  /** Memory while answering, which is always more than the download. */
  estimatedBytes: number
  recommended: boolean
  fitReason: string | null
  /** Small local models are chat-only; tools stay off unless declared. */
  supportsTools: boolean
  /**
   * What the capabilities say the model is for, computed by the server.
   * Optional because a server built before the field sends none, and the page
   * falls back to reading the capabilities itself.
   */
  category?: string
}

export interface Gpu {
  vendor: string
  name: string
  vramBytes: number | null
}

export interface DeviceCapabilities {
  source: string
  platform: string
  architecture: string
  cpuCores: number
  cpuModel: string | null
  totalMemoryBytes: number
  availableMemoryBytes: number
  freeDiskBytes: number
  gpus: Gpu[]
  unifiedMemory: boolean
  detectedAt: string
}

export interface ModelFit {
  modelId: string
  tier: FitTier
  estimatedBytes: number
  downloadBytes: number
  onGpu: boolean
  recommended: boolean
  reason: string
}

/** Live download progress reported by the backend. */
export interface PullProgress {
  modelId: string
  phase: string
  completed: number
  total: number
  percent: number
  finished: boolean
  error?: string
}

export interface ModelRuntimeStatus {
  agentConnected: boolean
  runtimeInstalled: boolean
  runtimeRunning: boolean
  version: string | null
  message: string | null
}

/** Mirrors com.nevtan.orchestrator.StreamEvent. */
export type StreamEventType =
  | 'TEXT_DELTA'
  | 'MESSAGE_SAVED'
  | 'TASK_UPDATED'
  | 'TOOL_EXECUTION'
  | 'TOOL_PROGRESS'
  | 'APPROVAL_REQUIRED'
  | 'APPROVAL_RESOLVED'
  | 'DONE'
  | 'ERROR'

export interface StreamEvent {
  type: StreamEventType
  conversationId: string
  taskId: string | null
  text: string | null
  data: Record<string, unknown> | null
  at: string
}

/** UI-side view of a tool call, assembled from TOOL_EXECUTION events. */
export interface ToolRun {
  toolExecutionId: string
  tool: string
  status: ToolExecutionStatus
  riskLevel: PermissionLevel
  arguments: Record<string, unknown>
  result?: string
  exitCode?: number
}

export interface PendingApproval {
  approvalId: string
  toolExecutionId: string
  tool: string
  riskLevel: PermissionLevel
  description: string
  arguments: Record<string, unknown>
}

export interface TaskStep {
  name: string
  status: string
}

export interface TaskState {
  taskId: string
  status: TaskStatus
  currentStep: number
  totalSteps: number
  steps: TaskStep[]
}

/**
 * Whether an installed model still matches what its publisher serves.
 *
 * `unknown` is its own answer, not a synonym for up to date: the publisher
 * could not be reached, so nothing should be re-downloaded on the strength of
 * it.
 */
export type ModelUpdateState = 'UP_TO_DATE' | 'UPDATE_AVAILABLE' | 'UNKNOWN'

export interface ModelUpdateStatus {
  modelId: string
  name: string
  state: ModelUpdateState
  installedDigest: string | null
  upstreamDigest: string | null
  upstreamSizeBytes: number
  checkedAt: string
}

export interface ModelUpdates {
  models: ModelUpdateStatus[]
  updateAvailable: boolean
}
